import type { ApprovalRequestReadModel, OrderTransactionSnapshot } from "@billiard/contracts";
import { isApprovalRequestReadModel, isOrderTransactionSnapshot } from "@billiard/contracts";

export type Sha256HexFunction = (canonicalValue: string) => string;
export type DeriveOrderTransactionSnapshotInput = Omit<
  OrderTransactionSnapshot,
  "businessFactSha256"
>;

export type ApprovedCashRefundPlanningErrorCode =
  | "INVALID_APPROVAL"
  | "APPROVAL_VERSION_MISMATCH"
  | "APPROVAL_NOT_APPROVED"
  | "APPROVAL_EXECUTION_NOT_READY"
  | "APPROVAL_SNAPSHOT_HASH_MISMATCH"
  | "INVALID_ORDER_TRANSACTION"
  | "ORDER_TRANSACTION_HASH_MISMATCH"
  | "ORDER_TRANSACTION_DRIFT"
  | "ORDER_IDENTITY_DRIFT"
  | "ORDER_STATUS_NOT_REFUNDABLE"
  | "PRODUCT_ITEMS_UNSUPPORTED"
  | "COACH_SERVICES_UNSUPPORTED"
  | "GROUP_BUY_UNSUPPORTED"
  | "PAYMENT_CARDINALITY_UNSUPPORTED"
  | "PAYMENT_METHOD_UNSUPPORTED"
  | "PAYMENT_STATUS_NOT_REFUNDABLE"
  | "AMOUNT_INCONSISTENT";

export interface ApprovedCashRefundPlan {
  kind: "ORDER_CASH_REFUND";
  approvalId: string;
  approvalVersion: number;
  approvalSnapshotSha256: string;
  orderTransactionBusinessFactSha256: string;
  tenantId: string;
  storeId: string;
  orderId: string;
  paymentId: string;
  amountFen: number;
  reason: string;
  expectedOrderStatus: "SETTLED" | "REFUNDED";
  expectedOrderRefundedFen: number;
  expectedPaymentStatus:
    | "CONFIRMED_LOCAL"
    | "CONFIRMED_CLOUD"
    | "MANUALLY_RECONCILED"
    | "PARTIALLY_REFUNDED";
  expectedPaymentRefundedFen: number;
  nextOrderStatus: "SETTLED" | "REFUNDED";
  nextOrderRefundedFen: number;
  nextPaymentStatus: "PARTIALLY_REFUNDED" | "REFUNDED";
  nextPaymentRefundedFen: number;
}

export type ApprovedCashRefundPlanResult =
  | { ok: true; plan: ApprovedCashRefundPlan }
  | {
      ok: false;
      error: { code: ApprovedCashRefundPlanningErrorCode; message: string };
    };

export interface PlanApprovedCashRefundInput {
  approval: ApprovalRequestReadModel;
  expectedApprovalVersion: number;
  transaction: OrderTransactionSnapshot;
}

/** Stable recursive JSON shared with the Gateway SHA-256 boundary. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value !== null && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

export function canonicalOrderTransactionFacts(
  value: DeriveOrderTransactionSnapshotInput | OrderTransactionSnapshot,
): string {
  const {
    businessFactSha256: _businessFactSha256,
    capturedAt: _capturedAt,
    ...businessFacts
  } = value as OrderTransactionSnapshot;
  return canonicalJson(businessFacts);
}

function normalizeIds(values: readonly string[]): string[] {
  return [...values].sort((left, right) => left.localeCompare(right));
}

export function deriveOrderTransactionSnapshot(
  input: DeriveOrderTransactionSnapshotInput,
  sha256Hex: Sha256HexFunction,
): OrderTransactionSnapshot {
  const normalized: DeriveOrderTransactionSnapshotInput = {
    ...input,
    orderItemIds: normalizeIds(input.orderItemIds),
    coachServiceIds: normalizeIds(input.coachServiceIds),
    groupBuyFulfillmentIds: normalizeIds(input.groupBuyFulfillmentIds),
    refundIds: normalizeIds(input.refundIds),
    payments: [...input.payments]
      .map((payment) => ({ ...payment }))
      .sort((left, right) => left.paymentId.localeCompare(right.paymentId)),
  };
  const snapshot: OrderTransactionSnapshot = {
    ...normalized,
    businessFactSha256: sha256Hex(canonicalOrderTransactionFacts(normalized)),
  };
  if (!isOrderTransactionSnapshot(snapshot)) {
    throw new TypeError("invalid canonical order transaction facts");
  }
  return deepFreeze(snapshot);
}

function failed(
  code: ApprovedCashRefundPlanningErrorCode,
  message: string,
): ApprovedCashRefundPlanResult {
  return { ok: false, error: { code, message } };
}

const refundablePaymentStatuses = new Set<ApprovedCashRefundPlan["expectedPaymentStatus"]>([
  "CONFIRMED_LOCAL",
  "CONFIRMED_CLOUD",
  "MANUALLY_RECONCILED",
  "PARTIALLY_REFUNDED",
]);

export function planApprovedCashRefund(
  input: PlanApprovedCashRefundInput,
  sha256Hex: Sha256HexFunction,
): ApprovedCashRefundPlanResult {
  const { approval, expectedApprovalVersion, transaction } = input;
  if (!isApprovalRequestReadModel(approval)) {
    return failed("INVALID_APPROVAL", "审批记录不完整，系统没有执行退款。请刷新审批记录后重试。");
  }
  if (approval.version !== expectedApprovalVersion) {
    return failed(
      "APPROVAL_VERSION_MISMATCH",
      "审批记录已经变化，系统没有执行退款。请刷新后按最新状态重试。",
    );
  }
  if (approval.status !== "APPROVED") {
    return failed("APPROVAL_NOT_APPROVED", "这笔申请尚未完成审批，系统没有执行退款。");
  }
  if (approval.execution.status !== "READY" && approval.execution.status !== "FAILED_RETRYABLE") {
    return failed(
      "APPROVAL_EXECUTION_NOT_READY",
      "这笔申请当前不能开始或重复执行，系统没有产生第二次退款。",
    );
  }
  if (approval.snapshot.kind !== "ORDER_CASH_REFUND") {
    return failed("INVALID_APPROVAL", "这笔审批不是订单现金退款，系统没有交给现金退款执行器处理。");
  }
  if (sha256Hex(canonicalJson(approval.snapshot)) !== approval.snapshotSha256) {
    return failed(
      "APPROVAL_SNAPSHOT_HASH_MISMATCH",
      "审批时保存的订单内容校验失败，系统没有执行退款。",
    );
  }
  if (!isOrderTransactionSnapshot(transaction)) {
    return failed("INVALID_ORDER_TRANSACTION", "订单资金事实不完整，系统没有执行退款。");
  }
  if (sha256Hex(canonicalOrderTransactionFacts(transaction)) !== transaction.businessFactSha256) {
    return failed("ORDER_TRANSACTION_HASH_MISMATCH", "订单资金事实校验失败，系统没有执行退款。");
  }
  const approved = approval.snapshot;
  if (approved.businessFactSha256 !== transaction.businessFactSha256) {
    return failed(
      "ORDER_TRANSACTION_DRIFT",
      "订单或付款在审批后已经变化，系统没有按旧审批执行退款。请重新申请。",
    );
  }
  if (
    approved.orderId !== transaction.orderId ||
    approved.orderNumber !== transaction.orderNumber ||
    approved.businessDate !== transaction.businessDate ||
    approved.tableNumber !== transaction.tableNumber
  ) {
    return failed("ORDER_IDENTITY_DRIFT", "审批对应的订单身份不一致，系统没有执行退款。");
  }
  if (transaction.orderStatus !== "SETTLED" && transaction.orderStatus !== "REFUNDED") {
    return failed(
      "ORDER_STATUS_NOT_REFUNDABLE",
      "订单当前状态不能退款，系统没有执行任何资金变化。",
    );
  }
  if (transaction.orderItemIds.length > 0) {
    return failed(
      "PRODUCT_ITEMS_UNSUPPORTED",
      "这笔订单含商品，商品库存恢复能力尚未接入统一退款，系统没有执行退款。",
    );
  }
  if (transaction.coachServiceIds.length > 0) {
    return failed(
      "COACH_SERVICES_UNSUPPORTED",
      "这笔订单含助教服务，服务与提成冲正能力尚未接入统一退款，系统没有执行退款。",
    );
  }
  if (transaction.groupBuyFulfillmentIds.length > 0) {
    return failed(
      "GROUP_BUY_UNSUPPORTED",
      "这笔订单含团购核销，团购冲正能力尚未接入统一退款，系统没有执行退款。",
    );
  }
  if (transaction.payments.length !== 1) {
    return failed(
      "PAYMENT_CARDINALITY_UNSUPPORTED",
      "这笔订单不是单笔付款，组合付款拆分能力尚未接入统一退款，系统没有执行退款。",
    );
  }
  const payment = transaction.payments[0];
  if (!payment) {
    return failed("PAYMENT_CARDINALITY_UNSUPPORTED", "订单付款事实为空，系统没有执行退款。");
  }
  if (payment.method !== "CASH") {
    return failed(
      "PAYMENT_METHOD_UNSUPPORTED",
      "首批统一退款只支持单笔现金付款，系统没有修改其他付款方式。",
    );
  }
  if (
    !refundablePaymentStatuses.has(
      payment.status as ApprovedCashRefundPlan["expectedPaymentStatus"],
    )
  ) {
    return failed("PAYMENT_STATUS_NOT_REFUNDABLE", "付款当前不能安全退款，系统没有产生资金变化。");
  }
  const paymentAvailableFen = payment.amountFen - payment.refundedFen;
  const orderAvailableFen = transaction.orderPaidFen - transaction.orderAlreadyRefundedFen;
  if (
    payment.amountFen !== transaction.orderPaidFen ||
    payment.refundedFen !== transaction.orderAlreadyRefundedFen ||
    approved.orderStatus !== transaction.orderStatus ||
    approved.orderPaidFen !== transaction.orderPaidFen ||
    approved.orderAlreadyRefundedFen !== transaction.orderAlreadyRefundedFen ||
    approved.paymentId !== payment.paymentId ||
    approved.paymentMethod !== payment.method ||
    approved.paymentAmountFen !== payment.amountFen ||
    approved.paymentAlreadyRefundedFen !== payment.refundedFen ||
    approved.paymentAvailableFen !== paymentAvailableFen ||
    approved.amountFen <= 0 ||
    approved.amountFen > paymentAvailableFen ||
    approved.amountFen > orderAvailableFen
  ) {
    return failed(
      "AMOUNT_INCONSISTENT",
      "审批金额、订单余额或付款余额不一致，系统没有执行退款。请重新申请。",
    );
  }
  const nextOrderRefundedFen = transaction.orderAlreadyRefundedFen + approved.amountFen;
  const nextPaymentRefundedFen = payment.refundedFen + approved.amountFen;
  return {
    ok: true,
    plan: {
      kind: "ORDER_CASH_REFUND",
      approvalId: approval.approvalId,
      approvalVersion: approval.version,
      approvalSnapshotSha256: approval.snapshotSha256,
      orderTransactionBusinessFactSha256: transaction.businessFactSha256,
      tenantId: transaction.tenantId,
      storeId: transaction.storeId,
      orderId: transaction.orderId,
      paymentId: payment.paymentId,
      amountFen: approved.amountFen,
      reason: approved.reason,
      expectedOrderStatus: transaction.orderStatus,
      expectedOrderRefundedFen: transaction.orderAlreadyRefundedFen,
      expectedPaymentStatus: payment.status as ApprovedCashRefundPlan["expectedPaymentStatus"],
      expectedPaymentRefundedFen: payment.refundedFen,
      nextOrderStatus: nextOrderRefundedFen === transaction.orderPaidFen ? "REFUNDED" : "SETTLED",
      nextOrderRefundedFen,
      nextPaymentStatus:
        nextPaymentRefundedFen === payment.amountFen ? "REFUNDED" : "PARTIALLY_REFUNDED",
      nextPaymentRefundedFen,
    },
  };
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object") {
    Object.freeze(value);
    for (const item of Object.values(value)) deepFreeze(item);
  }
  return value;
}
